import type { Sprite } from 'pixi.js';
import { GAME } from '@/config/game';
import type { BulletSystem } from './bullets';
import type { MinionKind } from './minion-defs';

/** Live minion state (MinionSystem owns the array — docs/12 M2). */
export interface Minion {
  kind: MinionKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  orbitAngle: number;
  fireTimer: number;
  animT: number;
  /** Per-kind scratch value (dasher zig side, sniper strafe dir, ...) */
  aux: number;
  /** Called in by the boss itself (hive waves, reinforcements) */
  summoned: boolean;
  sprite: Sprite;
}

/*
 * Shared minion steering + firing helpers — pure functions over a Minion so
 * minions.ts stays a lean system (≤300 lines, CLAUDE.md rule 1).
 */

export function chase(
  m: Minion, dx: number, dy: number, dist: number, accel: number, max: number, dt: number,
): void {
  m.vx += (dx / dist) * accel * dt;
  m.vy += (dy / dist) * accel * dt;
  const speed = Math.hypot(m.vx, m.vy);
  if (speed > max) {
    m.vx = (m.vx / speed) * max;
    m.vy = (m.vy / speed) * max;
  }
  m.x += m.vx * dt;
  m.y += m.vy * dt;
}

export function shoot(m: Minion, interval: number, dt: number): boolean {
  m.fireTimer -= dt;
  if (m.fireTimer <= 0) {
    m.fireTimer = interval;
    return true;
  }
  return false;
}

export function aimedShot(b: BulletSystem, m: Minion, px: number, py: number, speed: number): void {
  shotAt(b, m, Math.atan2(py - m.y, px - m.x), speed);
}

export function shotAt(
  b: BulletSystem, m: Minion, angle: number, speed: number,
  life = 4, texKey = 'bullet/enemy/ball',
): void {
  b.spawn({
    x: m.x, y: m.y, dirX: Math.cos(angle), dirY: Math.sin(angle), speed,
    life, radius: 9, damage: 1, friendly: false, texKey, rotates: false,
  });
}

/**
 * Keep a minion inside the arena's wall band (the REAL layout size): snipers
 * retreated off-map and only the 75 s wave failsafe ever cleared them.
 */
export function clampToArena(
  m: Minion, b: { x: number; y: number; w: number; h: number }, radius: number,
): void {
  const inset = GAME.arena.wallThickness + radius;
  m.x = Math.max(b.x + inset, Math.min(b.x + b.w - inset, m.x));
  m.y = Math.max(b.y + inset, Math.min(b.y + b.h - inset, m.y));
}

/** Death spray: kamikaze flies burst into 3 bullets toward the player. */
export function deathSpray(bullets: BulletSystem, m: Minion, px: number, py: number): void {
  const base = Math.atan2(py - m.y, px - m.x);
  for (const off of [-0.45, 0, 0.45]) {
    shotAt(bullets, m, base + off, 200);
  }
}

